import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahClaims } from '~constants/api';
import { VALIDATION_TIMEOUT_MS } from '~constants/common';

import { Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import { Claim, ClaimItem } from '~support-at-home/models';

// Track validation timeouts per claim
const validationTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export const typePrefix = 'claims';

// TODO: Strict Req / Res types
interface ClaimCreateResponse {
  claim: Claim;
}

interface ClaimItemsResponse {
  rows: ClaimItem[];
  count: number;
  total: number;
}

export const claimsApi = createApi({
  reducerPath: 'claimsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahClaims }),
  tagTypes: ['claims', 'claims-estimates', 'claim-items'],
  endpoints: (builder) => ({
    getClaims: builder.query<Claim[], Request | void>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [{ type: 'claims', id: 'LIST' }],
      transformResponse: (payload) => unwrap<Claim[]>(payload),
    }),
    getClaim: builder.query<Claim, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}`, method: 'GET', params: rest }),
      providesTags: (_result, _error, { id }) => [{ type: 'claims', id }],
      transformResponse: (payload) => unwrap<Claim>(payload),
    }),
    getClaimsEstimate: builder.query<any, Request>({
      query: (params) => ({ url: '/estimate', method: 'GET', params }),
      providesTags: [{ type: 'claims-estimates', id: 'LIST' }],
      transformResponse: (payload) => unwrap<any>(payload),
    }),
    createClaim: builder.mutation<ClaimCreateResponse, Request>({
      query: (body) => ({ url: '', method: 'POST', body }),
      invalidatesTags: [
        { type: 'claims', id: 'LIST' },
        { type: 'claims-estimates', id: 'LIST' },
      ],
      transformResponse: (payload) => unwrap<ClaimCreateResponse>(payload),
    }),
    deleteClaim: builder.mutation<any, Request & { id: string }>({
      query: ({ id }) => ({ url: `/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error) => [{ type: 'claims' }],
      transformResponse: (payload) => unwrap<any>(payload),
    }),
    finaliseClaim: builder.mutation<any, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}/finalise`, method: 'POST', body: rest }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'claims', id }],
      transformResponse: (payload) => unwrap<any>(payload),
    }),
    unfinaliseClaim: builder.mutation<any, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}/unfinalise`, method: 'POST', body: rest }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'claims', id }],
      transformResponse: (payload) => unwrap<any>(payload),
    }),
    cancelValidation: builder.mutation<any, Request & { id: string }>({
      query: ({ id }) => ({ url: `/${id}/cancel-validation`, method: 'POST' }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'claims', id: 'LIST' },
        { type: 'claims', id },
      ],
      transformResponse: (payload) => unwrap<any>(payload),
    }),
    validateClaim: builder.mutation<any, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}/validate`, method: 'POST', body: rest }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'claims', id: 'LIST' },
        { type: 'claims', id },
      ],
      transformResponse: (payload) => unwrap<any>(payload),
      async onQueryStarted({ id }, { dispatch, getState, queryFulfilled }) {
        // Clear any existing timeout for this claim (if re-validating)
        const existingTimeout = validationTimeouts.get(String(id));
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        // Optimistically update individual claim query for detail view
        const patchClaim = dispatch(
          claimsApi.util.updateQueryData('getClaim', { id }, (draft) => {
            draft.is_validating = true;
          }),
        );

        // Optimistically update claim list query for snappy UX response
        const listPatches = claimsApi.util
          .selectInvalidatedBy(getState(), [{ type: 'claims', id: 'LIST' }])
          .filter(({ endpointName }) => endpointName === 'getClaims')
          .map(({ originalArgs }) =>
            dispatch(
              claimsApi.util.updateQueryData('getClaims', originalArgs, (draft) => {
                const claim = draft.find((c) => String(c.id) === String(id));
                if (claim) {
                  claim.is_validating = true;
                }
              }),
            ),
          );

        // Hard timeout to abort validation state and stop polling after 3 minutes
        const timeoutId = setTimeout(() => {
          validationTimeouts.delete(String(id));

          // call cancel validation endpoint internally
          dispatch(claimsApi.endpoints.cancelValidation.initiate({ id }));
        }, VALIDATION_TIMEOUT_MS);

        validationTimeouts.set(String(id), timeoutId);

        try {
          await queryFulfilled;
        } catch {
          clearTimeout(timeoutId);
          validationTimeouts.delete(String(id));
          patchClaim.undo();
          listPatches.forEach((patch) => patch.undo());
        }
      },
    }),

    // claim item
    getClaimItems: builder.query<ClaimItemsResponse, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}/items`, method: 'GET', params: rest }),
      providesTags: (_result, _error, { id }) => [{ type: 'claim-items', id: `CLAIM_${id}` }],
      transformResponse: (payload) => unwrap<ClaimItemsResponse>(payload),
    }),
    getClaimItem: builder.query<ClaimItem, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/items/${id}`, method: 'GET', params: rest }),
      providesTags: (_result, _error, { id }) => [{ type: 'claim-items', id }],
      transformResponse: (payload) => unwrap<ClaimItem>(payload),
    }),
    updateClaimItem: builder.mutation<ClaimItem, { id: string; claim_id: string; changes: Partial<ClaimItem> }>({
      query: ({ id, changes }) => ({
        url: `/items/${id}`,
        method: 'PATCH',
        body: changes,
      }),
      invalidatesTags: (_result, _error, { claim_id }) => [
        { type: 'claims', id: claim_id },
        { type: 'claim-items', id: `CLAIM_${claim_id}` },
      ],
    }),
    bulkUpdateClaimItems: builder.mutation<void, { ids: string[]; claim_id: string; changes: Partial<ClaimItem> }>({
      query: ({ ids, changes }) => ({
        url: `/items/bulk-update`,
        method: 'POST',
        body: { item_ids: ids, changes },
      }),
      invalidatesTags: (_result, _error, { claim_id }) => [
        { type: 'claims', id: claim_id },
        { type: 'claim-items', id: `CLAIM_${claim_id}` },
      ],
    }),
    deleteClaimItem: builder.mutation<void, { id: string; claim_id: string }>({
      query: ({ id }) => ({
        url: `/items/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { claim_id }) => [{ type: 'claim-items', id: `CLAIM_${claim_id}` }],
      transformResponse: (payload) => unwrap<void>(payload),
    }),
    bulkDeleteClaimItems: builder.mutation<void, { ids: string[]; claim_id: string }>({
      query: ({ ids }) => ({
        url: `/items/bulk-delete`,
        method: 'POST',
        body: { item_ids: ids },
      }),
      invalidatesTags: (_result, _error, { claim_id }) => [
        { type: 'claims', id: claim_id },
        { type: 'claim-items', id: `CLAIM_${claim_id}` },
      ],
    }),
  }),
});

export const {
  useGetClaimsQuery,
  useGetClaimQuery,
  useGetClaimsEstimateQuery,
  useCreateClaimMutation,
  useDeleteClaimMutation,
  useFinaliseClaimMutation,
  useUnfinaliseClaimMutation,
  useValidateClaimMutation,

  useGetClaimItemsQuery,
  useGetClaimItemQuery,
  useUpdateClaimItemMutation,
  useBulkUpdateClaimItemsMutation,
  useDeleteClaimItemMutation,
  useBulkDeleteClaimItemsMutation,
} = claimsApi;
